import {
  MarkdownEditHistoryViewer,
  type MarkdownEditHistoryViewerCommentFragment,
} from '@github-ui/markdown-edit-history-viewer/MarkdownEditHistoryViewer'
import {
  MarkdownLastEditedBy,
  type MarkdownLastEditedByFragment,
} from '@github-ui/markdown-edit-history-viewer/MarkdownLastEditedBy'
import {useMarkdownEditHistoryViewerQuery} from '@github-ui/markdown-edit-history-viewer/use-markdown-edit-history-viewer-query'
import {Suspense} from 'react'

import {ActivityHeader, type CommentAuthorType} from './ActivityHeader'
import {CommentActions, type CommentActionsProps} from './CommentActions'

export type CommentSubjectTypes = 'pull request' | 'issue' | 'commit'

export type CommentHeaderProps = Omit<CommentActionsProps, 'editHistoryComponent'> & {
  comment: CommentActionsProps['comment'] & MarkdownLastEditedByFragment & MarkdownEditHistoryViewerCommentFragment
  commentAuthorProfileName?: string | null
  avatarUrl: string
  userAvatar?: React.ReactElement
  additionalHeaderMessage?: React.ReactElement
  viewerDidAuthor?: boolean
  /**
   * The login of the author of the subject of the comment.
   *
   * e.g. the login of the author of the issue, pull request, commit the comment is on.
   */
  commentSubjectAuthorLogin?: string
  hideActions?: boolean
  isReply?: boolean
  commentAuthorType?: CommentAuthorType
  /**
   * The type of the subject that the comment is associated to.
   * Used as display text in the subject author tooltip.
   */
  commentSubjectType?: CommentSubjectTypes
  headingProps?: {
    as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  }
  lazyFetchEditHistory?: boolean
  id?: string
  forceInlineAvatar?: boolean
  pinComment?: (commentId: string) => void
  unpinComment?: (commentId: string) => void
  pinnedByLogin?: string | null
}

export function CommentHeader({hideActions, lazyFetchEditHistory = false, onEditHover, ...props}: CommentHeaderProps) {
  const editHistoryComponent = lazyFetchEditHistory ? (
    <Suspense fallback={null}>
      <CommentHeaderEditHistory id={props.comment.id} />
    </Suspense>
  ) : (
    <MarkdownEditHistoryViewer
      editHistory={props.comment}
      renderAnchor={() => <MarkdownLastEditedBy editInformation={props.comment} />}
    />
  )

  return (
    <ActivityHeader
      editHistoryComponent={editHistoryComponent}
      {...props}
      actions={hideActions ? undefined : <CommentActions {...props} onEditHover={onEditHover} />}
    />
  )
}

export function CommentHeaderEditHistory({id}: {id: string}) {
  const data = useMarkdownEditHistoryViewerQuery({id})
  return data ? (
    <MarkdownEditHistoryViewer
      editHistory={data}
      renderAnchor={() => <MarkdownLastEditedBy editInformation={data} />}
    />
  ) : null
}
